import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { full_name: "", email: "", account_email: "", reason: "" };

export default function DataDeletion() {
  const [form, setForm] = useState(empty);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    if (!confirm) {
      toast.error("Please confirm you want to delete your data.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/data-deletion`, { ...form, confirm: true });
      setDone(true);
      toast.success("Your deletion request has been received.");
      setForm(empty);
      setConfirm(false);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Data Deletion Request"
        description="Request the deletion of your Kinect Nova AI account and associated personal data. GDPR and CCPA compliant."
        path="/data-deletion"
      />
      <PageHeader
        eyebrow="Your data, your control"
        title="Request data deletion"
        subtitle="You can request the permanent deletion of your Kinect Nova AI account and all associated personal data at any time."
      />

      <section className="mx-auto max-w-3xl px-5 py-16 sm:px-8 lg:py-24">
        <Reveal>
          <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              In line with the GDPR, CCPA and the requirements of the Apple App Store and
              Google Play, you have the right to request deletion of your personal data.
              When we receive your request:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>We verify your identity to protect your account.</li>
              <li>We permanently delete your profile, health records, messages and associated data.</li>
              <li>We complete deletion within 30 days and confirm by email.</li>
              <li>Certain records may be retained only where required by law.</li>
            </ul>
            <p>
              You can also delete your account directly in the app under{" "}
              <strong>Settings → Account → Delete account</strong>, or submit the form below.
            </p>
          </div>
        </Reveal>

        {done ? (
          <Reveal className="mt-10 rounded-3xl border border-primary/30 bg-primary/5 p-8 text-center" data-testid="deletion-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-foreground">Request received</h2>
            <p className="mt-2 text-muted-foreground">
              We've received your data deletion request and will email you a confirmation shortly.
            </p>
          </Reveal>
        ) : (
          <Reveal delay={0.1} className="mt-10">
            <form
              onSubmit={submit}
              data-testid="deletion-form"
              className="rounded-3xl border border-border bg-card p-7 sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="full_name" className="text-sm font-medium">Full name</Label>
                  <Input id="full_name" data-testid="deletion-name-input" value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" className="mt-2 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Contact email</Label>
                  <Input id="email" type="email" data-testid="deletion-email-input" value={form.email} onChange={update("email")} placeholder="jane@example.com" className="mt-2 rounded-xl" />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="account_email" className="text-sm font-medium">Account email (if different)</Label>
                <Input id="account_email" type="email" data-testid="deletion-account-input" value={form.account_email} onChange={update("account_email")} placeholder="account@example.com" className="mt-2 rounded-xl" />
              </div>
              <div className="mt-5">
                <Label htmlFor="reason" className="text-sm font-medium">Reason (optional)</Label>
                <Textarea id="reason" data-testid="deletion-reason-input" value={form.reason} onChange={update("reason")} rows={4} placeholder="Help us improve — why are you leaving?" className="mt-2 rounded-xl" />
              </div>
              <label className="mt-6 flex items-start gap-3">
                <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} data-testid="deletion-confirm-checkbox" className="mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  I understand this will permanently delete my account and personal data, and this action cannot be undone.
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="deletion-submit-btn"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-destructive px-6 py-3.5 text-sm font-semibold text-destructive-foreground transition-all hover:opacity-90 disabled:opacity-60 sm:w-auto"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {loading ? "Submitting..." : "Submit deletion request"}
              </button>
              <p className="mt-4 text-xs text-muted-foreground">
                Prefer email? Send your request to {SITE.supportEmail}.
              </p>
            </form>
          </Reveal>
        )}
      </section>
    </>
  );
}
